import React, { useState, useEffect } from "react";
import { Button, Spin, Tooltip, Alert, message } from "antd";
import {
  TruckOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { BASE_URL, API } from "../../../utils/constants";

const DHLShippingCalculator = ({
  products,
  originAddress,
  destinationAddress,
  onShippingCalculated,
  onFetchAddress,
  onAddressLoaded,
  isRequired = true,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(null);
  const [shippingService, setShippingService] = useState(null);
  const [error, setError] = useState(null);
  const [calculated, setCalculated] = useState(false);
  const [currentDestinationAddress, setCurrentDestinationAddress] =
    useState(destinationAddress);

  useEffect(() => {
    if (
      currentDestinationAddress &&
      currentDestinationAddress.zipCode &&
      !calculated &&
      !loading
    ) {
      setTimeout(() => {
        calculateShipping();
      }, 100);
    }
  }, [currentDestinationAddress]);

  const getAuthHeaders = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const token = user?.token || localStorage.getItem("token");
    return {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    };
  };

  const calculateTotalWeight = () => {
    if (!products || products.length === 0) return 0;

    const totalWeight = products.reduce((total, product) => {
      const productWeight = product.weight || 0.5;
      const quantity = product.quantity || 1;
      return total + productWeight * quantity;
    }, 0);

    return totalWeight;
  };

  const getStandardDimensions = () => {
    const dimensions = { length: 20, width: 20, height: 15 };
    return dimensions;
  };

  const fetchAndSetAddress = async () => {
    if (onFetchAddress && !currentDestinationAddress) {
      setLoading(true);
      try {
        const address = await onFetchAddress();
        if (address) {
          setCurrentDestinationAddress(address);
          if (onAddressLoaded) {
            onAddressLoaded(address);
          }
          return address;
        }
      } catch (error) {
        console.error("Error fetching address:", error);
        message.error(
          "No se pudo obtener tu dirección. Por favor verifica tu perfil."
        );
      } finally {
        setLoading(false);
      }
    }
    return currentDestinationAddress;
  };

  const calculateShipping = async () => {
    let addressToUse = currentDestinationAddress;

    if (!addressToUse && onFetchAddress) {
      setLoading(true);
      try {
        addressToUse = await fetchAndSetAddress();
      } finally {
        setLoading(false);
      }
    }

    if (!addressToUse) {
      const errorMsg =
        "No se encontró tu dirección. Por favor verifica tu perfil.";
      setError(errorMsg);
      message.error(errorMsg);
      return;
    }

    if (!addressToUse.zipCode || addressToUse.zipCode === "") {
      const errorMsg =
        "Tu dirección está incompleta. Por favor actualiza tu dirección en tu perfil.";
      setError(errorMsg);
      message.error(errorMsg);
      return;
    }

    if (!products || products.length === 0) {
      const errorMsg = "No hay productos en el carrito";
      setError(errorMsg);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const totalWeight = calculateTotalWeight();

      if (totalWeight === 0) {
        throw new Error("No se pudo calcular el peso de los productos");
      }

      const payload = {
        origin_country: originAddress?.countryCode || "MX",
        origin_postal_code: originAddress?.zipCode || "06600",
        origin_city: originAddress?.city || "Ciudad de Mexico",
        destination_country: addressToUse.countryCode || "MX",
        destination_postal_code: addressToUse.zipCode,
        destination_city: addressToUse.city || "",
        weight: totalWeight,
        dimensions: getStandardDimensions(),
      };

      const quoteUrl = `${BASE_URL}/${API.MARKETPLACE.DHL.QUOTE}`;
      const response = await axios.post(quoteUrl, payload, {
        headers: getAuthHeaders(),
      });

      if (
        response.data.success &&
        response.data.pricing &&
        response.data.pricing.length > 0
      ) {
        const price = response.data.pricing[0]?.totalPrice[0]?.price || 0;
        const serviceName =
          response.data.pricing[0]?.productName || "DHL Express";

        setShippingCost(price);
        setShippingService(serviceName);
        setCalculated(true);

        if (onShippingCalculated) {
          const shippingData = {
            cost: price,
            service: serviceName,
            calculated: true,
            weight: totalWeight,
            trackingNumber: null,
            quoteId: response.data.pricing[0]?.id,
            estimatedDelivery: response.data.pricing[0]?.estimatedDelivery,
          };
          onShippingCalculated(shippingData);
        }

        message.success(`Costo de envío calculado: $${price.toFixed(2)} MXN`);
      } else {
        throw new Error("No se pudo obtener el costo de envío");
      }
    } catch (error) {
      let errorMessage = "Error al calcular el costo de envío";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (errorMessage.toLowerCase().includes("invalid postal code")) {
        errorMessage =
          "El código postal no es válido. Por favor verifica tu dirección en el perfil.";
      } else if (errorMessage.toLowerCase().includes("service not available")) {
        errorMessage =
          "El servicio de envío no está disponible para esta ubicación.";
      } else if (errorMessage.toLowerCase().includes("weight")) {
        errorMessage = "El peso del paquete excede el límite permitido.";
      } else if (errorMessage.toLowerCase().includes("not configured")) {
        errorMessage =
          "El servicio de envío está en configuración. Por favor intenta más tarde.";
      }

      setError(errorMessage);
      setCalculated(false);

      if (onShippingCalculated) {
        onShippingCalculated({
          cost: null,
          service: null,
          calculated: false,
          error: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (disabled) {
    return null;
  }

  if (calculated && shippingCost !== null) {
    return (
      <div className="dhl-shipping-calculator calculated">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px",
            backgroundColor: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: "8px",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div style={{ flex: 1 }}>
            <CheckCircleOutlined
              style={{ color: "#52c41a", marginRight: "8px" }}
            />
            <strong>Costo de Envío DHL:</strong>
            <span
              style={{
                marginLeft: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                color: "#52c41a",
              }}
            >
              ${shippingCost.toFixed(2)} MXN
            </span>
            {shippingService && (
              <span
                style={{ marginLeft: "8px", fontSize: "12px", color: "#666" }}
              >
                ({shippingService})
              </span>
            )}
          </div>
          <Tooltip title="Recalcular costo">
            <Button
              size="small"
              icon={<CalculatorOutlined />}
              onClick={calculateShipping}
              loading={loading}
            >
              Recalcular
            </Button>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <div className="dhl-shipping-calculator">
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: "12px" }}
        />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px",
          backgroundColor: "#fafafa",
          border: "1px solid #e8e8e8",
          borderRadius: "8px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <TruckOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
          <span>Calcula el costo de envío con DHL</span>
          {isRequired && !calculated && (
            <span
              style={{ marginLeft: "8px", color: "#ff4d4f", fontSize: "12px" }}
            >
              (Requerido)
            </span>
          )}
        </div>
        <Tooltip
          title={
            isRequired && !calculated
              ? "Calcula el costo de envío antes de continuar"
              : "Calcular costo de envío"
          }
          placement="left"
        >
          <Button
            type={isRequired && !calculated ? "primary" : "default"}
            icon={loading ? <Spin /> : <CalculatorOutlined />}
            onClick={calculateShipping}
            loading={loading}
            disabled={loading}
          >
            {loading ? "Calculando..." : "Calcular Envío"}
          </Button>
        </Tooltip>
      </div>

      {!calculated && !error && (
        <div style={{ marginTop: "8px", fontSize: "12px", color: "#999" }}>
          <TruckOutlined /> El costo se calculará según el peso y destino de tu
          pedido
        </div>
      )}
    </div>
  );
};

export default DHLShippingCalculator;
